"use client";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useRelativeTime } from "@/hooks/use-relative-time";
import type { RecipeBoxInvitation } from "@/lib/types";
import { Bell } from "lucide-react";

type PendingInvitationsSheetProps = {
  invitations: RecipeBoxInvitation[];
  loading: boolean;
  errorMessage: string | null;
  respondingToken: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: (token: string | undefined) => void;
  onDecline: (token: string | undefined) => void;
};

export function PendingInvitationsSheet({
  invitations,
  loading,
  errorMessage,
  respondingToken,
  isOpen,
  onOpenChange,
  onAccept,
  onDecline,
}: PendingInvitationsSheetProps) {
  if (errorMessage) {
    return null;
  }

  if (loading && invitations.length === 0) {
    return null;
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Pending Invitations
          </SheetTitle>
          <SheetDescription>
            Accept these invitations to add recipe boxes to your dashboard.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {invitations.map((invitation) => (
            <PendingInvitationItem
              key={invitation.id}
              invitation={invitation}
              onAccept={() => {
                if (!invitation.token) {
                  return;
                }
                onAccept(invitation.token);
              }}
              onDecline={() => {
                if (!invitation.token) {
                  return;
                }
                onDecline(invitation.token);
              }}
              disabled={respondingToken === invitation.token}
            />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

type PendingInvitationItemProps = {
  invitation: RecipeBoxInvitation;
  onAccept: () => void;
  onDecline: () => void;
  disabled: boolean;
};

function PendingInvitationItem({
  invitation,
  onAccept,
  onDecline,
  disabled,
}: PendingInvitationItemProps) {
  const createdLabel = useRelativeTime(invitation.createdAt ?? null);
  const expiresLabel = useRelativeTime(invitation.expiresAt ?? null);

  const hasValidToken = !!invitation.token;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4">
      <div className="flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium">Invitation to join a recipe box</p>
          {!hasValidToken && (
            <Badge variant="destructive" className="text-xs">
              Invalid Token
            </Badge>
          )}
        </div>
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>
            Role: <span className="font-medium text-foreground">{invitation.role}</span>
          </p>
          {createdLabel && <p>Invited {createdLabel}</p>}
          {expiresLabel && <p>Expires {expiresLabel}</p>}
          <p className="text-xs">Recipe box ID: {invitation.recipeBoxId}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          onClick={onDecline}
          disabled={disabled || !hasValidToken}
          className="w-full sm:w-auto"
        >
          {disabled ? "Working…" : "Decline"}
        </Button>
        <Button
          onClick={onAccept}
          disabled={disabled || !hasValidToken}
          className="w-full sm:w-auto"
        >
          {disabled ? "Accepting…" : "Accept"}
        </Button>
      </div>
    </div>
  );
}

